// eslint-disable-next-line no-unused-vars
import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import consumableApi from './consumable.api';
import togglePublishedState from '~/api/togglePublishedState.api';
// eslint-disable-next-line no-unused-vars
import { alertSwal, fakeSuccessResponse } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const fetchConsumableApi = createAsyncThunk(
    'itemSaga/consumable/fetchConsumableApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await consumableApi.index();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        }
    },
);

export const toggleConsumablePublishedStateApi = createAsyncThunk(
    'itemSaga/consumable/toggleConsumablePublishedStateApi',
    async (id, { dispatch, rejectWithValue }) => {
        try {
            await togglePublishedState(id);

            return id;
        } catch (error) {
            handleThunkException(error, dispatch);

            return rejectWithValue([]);
        }
    },
);

export const createConsumableApi = createAsyncThunk(
    'itemSaga/consumable/createConsumableApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await consumableApi.create();

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const storeConsumableApi = createAsyncThunk(
    'itemSaga/consumable/storeConsumableApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values } = params;

        try {
            dispatch(requestStarted());
            const hero = await consumableApi.store(values);
            alertSwal('success', 'Consumable created successfully!');

            return hero;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const showConsumableApi = createAsyncThunk(
    'itemSaga/consumable/showConsumableApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const { data } = await consumableApi.show(id);

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updateConsumableApi = createAsyncThunk(
    'itemSaga/consumable/updateConsumableApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, id, values } = params;

        try {
            dispatch(requestStarted());
            await consumableApi.update(id, values);
            // await fakeSuccessResponse();
            alertSwal('success', 'Consumable updated successfully!');

            return values;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const deleteConsumableApi = createAsyncThunk(
    'itemSaga/consumable/deleteConsumableApi',
    async (params, { dispatch }) => {
        const { navigate, id } = params;

        try {
            await consumableApi.delete(id);

            return id;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);
        }
    },
);

export const consumableSlice = createSlice({
    name: 'itemSaga/consumable',
    initialState: {
        rows: [],
        model: {},
        editedModel: {},
        rarities: [],
        pictureAttributeNames: [],
        consumableEffects: [],
    }, //model is initialValues that are used for formik.
    reducers: {
        clearEditedConsumable(state) {
            state.editedModel = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchConsumableApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload,
                };
            })
            .addCase(fetchConsumableApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(toggleConsumablePublishedStateApi.fulfilled, (state, { payload }) => {
                const updateRow = state.rows.find((row) => row.id === payload);
                updateRow.isReady = !updateRow.isReady;
            })
            .addCase(toggleConsumablePublishedStateApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(createConsumableApi.fulfilled, (state, { payload }) => {
                state.model = payload.model;
                state.rarities = payload.rarities;
                state.pictureAttributeNames = payload.pictureAttributeNames;
                state.consumableEffects = payload.consumableEffects;
            })
            .addCase(createConsumableApi.rejected, () => {
                //
            });

        builder
            .addCase(storeConsumableApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storeConsumableApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showConsumableApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload.model;
                state.rarities = payload.rarities;
                state.pictureAttributeNames = payload.pictureAttributeNames;
                state.consumableEffects = payload.consumableEffects;
            })
            .addCase(showConsumableApi.rejected, () => {
                //
            });

        builder
            .addCase(updateConsumableApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make Update component re-render so that the checkDuplicateName() will have the latest `initialName`.
                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.id);

                updatedRow.name = payload.name;
                updatedRow.description = payload.description;
                const selectedRarity = state.rarities.find((item) => {
                    return payload.rarity.includes(item.id);
                });
                updatedRow.rarity = selectedRarity.name;
            })
            .addCase(updateConsumableApi.rejected, () => {
                //
            });

        builder.addCase(deleteConsumableApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});

export const { clearEditedConsumable } = consumableSlice.actions;
